import { c as _c } from "react/compiler-runtime";
import type { FileAttachment } from '@newfront-insurance/nova-core';
import { createContext, useContext, useState, useCallback, type ReactNode, useMemo } from 'react';

/**
 * Context for sharing attachment state across components.
 */
interface AttachmentsContextValue {
  attachments: Array<FileAttachment>;
  setAttachments: React.Dispatch<React.SetStateAction<Array<FileAttachment>>>;
  addAttachments: (newAttachments: Array<FileAttachment>) => void;
  removeAttachment: (index: number) => void;
  clearAttachments: () => void;
  updateAttachment: (index: number, updates: Partial<FileAttachment>) => void;
}
const AttachmentsContext = createContext<AttachmentsContextValue | undefined>(undefined);

/**
 * Provider for attachment state.
 * This allows multiple components (ChatAttachments, ChatAttachmentsPreview, etc.)
 * to share the same attachment state.
 */
export function AttachmentsProvider(t0) {
  const $ = _c(10);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [attachments, setAttachments] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = newAttachments => {
      setAttachments(prev => [...prev, ...newAttachments]);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const addAttachments = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = index => {
      setAttachments(prev_0 => prev_0.filter((_, i) => i !== index));
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const removeAttachment = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setAttachments([]);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const clearAttachments = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (index_0, updates) => {
      setAttachments(prev_1 => prev_1.map((attachment, i_0) => i_0 === index_0 ? {
        ...attachment,
        ...updates
      } : attachment));
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const updateAttachment = t5;
  let t6;
  let t7;
  if ($[5] !== attachments) {
    t7 = {
      attachments,
      setAttachments,
      addAttachments,
      removeAttachment,
      clearAttachments,
      updateAttachment
    };
    $[5] = attachments;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  t6 = t7;
  const value = t6;
  let t8;
  if ($[7] !== children || $[8] !== value) {
    t8 = <AttachmentsContext.Provider value={value}>{children}</AttachmentsContext.Provider>;
    $[7] = children;
    $[8] = value;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  return t8;
}

/**
 * Hook for accessing attachment state.
 * Must be used within AttachmentsProvider.
 */
export function useAttachments() {
  const context = useContext(AttachmentsContext);
  if (!context) {
    throw new Error("useAttachments must be used within AttachmentsProvider");
  }
  return context;
}